import LocationOffOutlinedIcon from '@mui/icons-material/LocationOffOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { Box, Button, CircularProgress, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as UsOnlyImage } from '../../assets/image-us-only.svg';
import { AppWrapper } from '../../components/_mui';
import {
  CONTACT_SUPPORT_LABEL,
  CONTINUE_WITH_US_ADDRESS_LABEL,
  getUsOnlyTitle,
  HAVE_US_ADDRESS_BODY,
  HAVE_US_ADDRESS_TITLE,
  NEED_HELP_TEXT,
  SUPPORT_EMAIL,
  US_COUNTRY,
  US_ONLY_CHOSEN_LEAD,
  US_ONLY_DETECTED_LEAD,
  US_ONLY_SUBTITLE,
  US_ONLY_UNKNOWN_LOCATION_TEXT,
} from '../../constants/country';
import { changeUserCountry } from '../../store/actions/init';
import { selectContentBackgroundColor } from '../../store/selectors/derived';
import {
  selectCountryReason,
  selectEffectiveCountryName,
  selectIsCountryChanging,
  selectUsOnlyCompanyName,
} from '../../store/selectors/init';
import { CountryReasonEnum } from '../../types/country';
import { getPrimaryTextColor, getSecondaryTextColor } from '../../util/ui';

const IMAGE_WIDTH = 160;
const INNER_MAX_WIDTH = 560;
const REASON_MAX_WIDTH = 320;
const ACTION_MAX_WIDTH = 480;
const SPINNER_SIZE = 16;

const wrapperSx: SxProps<Theme> = {
  flex: 1,
  justifyContent: 'center',
  pt: { xs: 3, sm: 6 },
  pb: { xs: 6, sm: 12 },
};

const innerSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: INNER_MAX_WIDTH,
  mx: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: 2,
};

const imageSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  mb: 1,
  color: 'primary.main',
  '& svg': { display: 'block', width: IMAGE_WIDTH, height: 'auto' },
};

const bodySx: SxProps<Theme> = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 3,
};

const headingGroupSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 };

const titleSx: SxProps<Theme> = { m: 0, fontSize: 28, lineHeight: 1.3, textWrap: 'pretty' };

const subtitleSx: SxProps<Theme> = { m: 0, fontSize: 16, lineHeight: '24px', textWrap: 'pretty' };

const reasonSx: SxProps<Theme> = {
  m: 0,
  maxWidth: REASON_MAX_WIDTH,
  fontSize: 14,
  lineHeight: '20px',
  textWrap: 'balance',
};

const reasonIconSx: SxProps<Theme> = { fontSize: 18, verticalAlign: -4, mr: 0.5, color: 'primary.main' };

const countrySx: SxProps<Theme> = { fontWeight: 'medium', whiteSpace: 'nowrap' };

const actionSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: ACTION_MAX_WIDTH,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
  mt: 1,
};

const actionTextSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 0.5 };

const actionTitleSx: SxProps<Theme> = { fontSize: 18, lineHeight: '24px' };

const actionBodySx: SxProps<Theme> = { fontSize: 14, lineHeight: '20px', textWrap: 'pretty' };

const supportSx: SxProps<Theme> = { mt: 1, fontSize: 14, lineHeight: '20px' };

const supportLinkSx: SxProps<Theme> = { fontWeight: 'medium' };

const UsOnlyScreen = () => {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  const companyName = useSelector(selectUsOnlyCompanyName);
  const reason = useSelector(selectCountryReason);
  const countryName = useSelector(selectEffectiveCountryName);
  const isCountryChanging = useSelector(selectIsCountryChanging);
  const contentBgColor = useSelector(selectContentBackgroundColor);

  const primaryTextColor = useMemo(() => getPrimaryTextColor(contentBgColor), [contentBgColor]);
  const secondaryTextColor = useMemo(() => getSecondaryTextColor(contentBgColor), [contentBgColor]);

  const title = getUsOnlyTitle(companyName);

  const reasonLine = useMemo(() => {
    const lead = reason === CountryReasonEnum.Chosen ? US_ONLY_CHOSEN_LEAD : US_ONLY_DETECTED_LEAD;

    if (reason === CountryReasonEnum.Unknown || !countryName) {
      return (
        <>
          <LocationOffOutlinedIcon aria-hidden sx={reasonIconSx} />
          {US_ONLY_UNKNOWN_LOCATION_TEXT}
        </>
      );
    }

    return (
      <>
        <LocationOnOutlinedIcon aria-hidden sx={reasonIconSx} />
        {`${lead} `}
        <Box component="span" sx={countrySx} color={primaryTextColor}>
          {countryName}
        </Box>
      </>
    );
  }, [reason, countryName, primaryTextColor]);

  const handleContinue = useCallback(() => {
    if (!isCountryChanging) {
      dispatch(changeUserCountry(US_COUNTRY));
    }
  }, [dispatch, isCountryChanging]);

  return (
    <AppWrapper sx={wrapperSx} data-testid="us-only-screen">
      <Box sx={innerSx}>
        <Box sx={imageSx}>
          <UsOnlyImage aria-hidden focusable="false" />
        </Box>
        <Box sx={bodySx}>
          <Box sx={headingGroupSx}>
            <Typography component="h1" fontWeight="medium" color={primaryTextColor} sx={titleSx}>
              {title}
            </Typography>
            <Typography component="p" color={secondaryTextColor} sx={subtitleSx}>
              {US_ONLY_SUBTITLE}
            </Typography>
            <Typography component="p" color={secondaryTextColor} sx={reasonSx}>
              {reasonLine}
            </Typography>
          </Box>
          <Box sx={actionSx}>
            <Box sx={actionTextSx}>
              <Typography component="span" fontWeight="medium" color={primaryTextColor} sx={actionTitleSx}>
                {HAVE_US_ADDRESS_TITLE}
              </Typography>
              <Typography component="span" color={secondaryTextColor} sx={actionBodySx}>
                {HAVE_US_ADDRESS_BODY}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              fullWidth={isMobileViewport}
              disabled={isCountryChanging}
              startIcon={isCountryChanging ? <CircularProgress size={SPINNER_SIZE} color="inherit" /> : undefined}
              onClick={handleContinue}
            >
              {CONTINUE_WITH_US_ADDRESS_LABEL}
            </Button>
          </Box>
        </Box>
        <Typography component="p" color={secondaryTextColor} sx={supportSx}>
          {`${NEED_HELP_TEXT} `}
          <Link href={`mailto:${SUPPORT_EMAIL}`} color="primary" underline="none" sx={supportLinkSx}>
            {CONTACT_SUPPORT_LABEL}
          </Link>
        </Typography>
      </Box>
    </AppWrapper>
  );
};

export default UsOnlyScreen;
